// Copyright (c) 2025 by Audere

import {
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { createStyleSheet } from "../utils/styles";
import { useParams } from "react-router-dom";
import { useConversationPatientSnapshot } from "../hooks/useConversationPatientSnapshot";
import { useTranslation } from "react-i18next";
import { HivInfo, PatientProfileItem } from "../features/api/labelApiSlice";
import { ErrorAlert, InfoAlert, WarningAlert } from "./Alerts";
import { ReactNode } from "react";
import { formatTimestamp } from "../utils/dates";
import { formatValueAsString } from "../utils/stringUtils";
import { getPersonaOption } from "../features/label/labelTypes";
import { useLabelingSchema } from "../hooks/useSchema";

interface ClientContextProps {
  persona?: string;
}

const ClientContext: React.FC<ClientContextProps> = ({ persona }) => {
  const { conversationUID } = useParams();
  const {
    patientData: data,
    isLoading,
    error,
  } = useConversationPatientSnapshot(conversationUID ?? "");
  const { t } = useTranslation();
  const { personas } = useLabelingSchema();
  const personaOption = getPersonaOption(persona, personas);

  const keyOrder = [
    "age",
    "biological_sex",
    "language",
    "occupation",
    "hiv_under_treatment",
    "recent_hiv_exposure",
    "takes_prep",
    "takes_pep",
    "requests_prep_or_pep",
    "condom_usage_frequency",
    "sex_trade_worker",
    "num_sexual_partners",
    "sex_with_hiv_positive",
    "sexually_abused",
    "physical_assault",
    "sti",
    "uti",
    "symptoms",
    "sex_under_influence",
    "num_female_partners",
    "num_male_partners",
  ];

  const renderHivInfo = (hivInfo: HivInfo) => {
    return (
      <List disablePadding>
        {Object.entries(hivInfo)
          .filter(([, value]) => value)
          .map(([key, value]) => (
            <ListItem key={key} disableGutters disablePadding>
              <Typography component="span">
                <Typography component="span" fontWeight="bold" color="info">
                  {`${key}: `}
                </Typography>
                {value}
              </Typography>
            </ListItem>
          ))}
      </List>
    );
  };

  const renderPatientProfile = (
    patientProfile: Partial<Record<string, PatientProfileItem>>
  ) => {
    // Identify keys missing from keyOrder
    const additionalKeys = Object.keys(patientProfile).filter(
      (key) => !keyOrder.includes(key)
    );

    const renderProfileItem = (key: string, item: PatientProfileItem) => {
      if (item.value == null) return null;

      const formattedValue = formatValueAsString(
        Array.isArray(item.value)
          ? item.value.map((valueItem) =>
              typeof valueItem === "object" && valueItem !== null
                ? valueItem.symptom
                : valueItem
            )
          : item.value
      );

      return (
        <ListItem key={key} disableGutters disablePadding>
          <Typography component="span">
            <Tooltip title={item.description} arrow>
              <Typography component="span" fontWeight="bold" color="info">
                {`${key}: `}
              </Typography>
            </Tooltip>
            {formattedValue}
          </Typography>
        </ListItem>
      );
    };

    return (
      <List disablePadding>
        {/* Render keyOrder items */}
        {keyOrder.map((key) => {
          const item = patientProfile[key];
          return item ? renderProfileItem(key, item) : null;
        })}
        {/* Render additional keys */}
        {additionalKeys.map((key) => {
          const item = patientProfile[key];
          return item ? renderProfileItem(key, item) : null;
        })}
      </List>
    );
  };

  if (isLoading)
    return <InfoAlert message={t("conversation.loadingClientContext")} />;
  if (error) return <ErrorAlert error={error} />;
  if (!data)
    return <WarningAlert message={t("conversation.noClientContext")} />;

  return (
    <Box sx={styles.clientContextBox}>
      <Typography
        variant="h5"
        textAlign="center"
        fontWeight="bold"
        gutterBottom
      >
        {t("conversation.clientContextTitle")}
      </Typography>
      <Typography
        variant="h6"
        textAlign="center"
        fontWeight="bold"
        gutterBottom
      >
        {data.preferred_name}
      </Typography>
      <Stack spacing={2}>
        <InfoCard title={t("conversation.hivInfoTitle")}>
          {data.hiv_info ? (
            renderHivInfo(data.hiv_info)
          ) : (
            <Typography data-testid="no-hiv-info">
              {t("conversation.noHivInfo")}
            </Typography>
          )}
        </InfoCard>
        <InfoCard title={t("conversation.patientProfileTitle")}>
          {data.patient_profile ? (
            renderPatientProfile(data.patient_profile)
          ) : (
            <Typography data-testid="no-patient-profile" gutterBottom>
              {t("conversation.noPatientProfile")}
            </Typography>
          )}
        </InfoCard>
        <InfoCard title={t("conversation.prevSummaryTitle")}>
          {data.previous_conversation_summary ? (
            <>
              <Typography color="textSecondary">
                {formatTimestamp(data.previous_conversation_summary.timestamp)}
              </Typography>
              <Typography>{data.previous_conversation_summary.text}</Typography>
            </>
          ) : (
            <Typography data-testid="no-prev-convo-summary">
              {t("conversation.noPrevSummary")}
            </Typography>
          )}
        </InfoCard>
        <InfoCard title={t("conversation.personaUsedTitle")}>
          {personaOption ? (
            <Box data-testid="persona-used">
              <Typography fontWeight="bold">{personaOption.name}</Typography>
              <Typography>{personaOption.description}</Typography>
            </Box>
          ) : (
            <Typography data-testid="no-persona-used">
              {t("conversation.noPersonaUsed")}
            </Typography>
          )}
        </InfoCard>
      </Stack>
    </Box>
  );
};

const InfoCard: React.FC<{ title: string; children: ReactNode }> = ({
  title,
  children,
}) => (
  <Card variant="outlined">
    <CardContent>
      <Typography variant="h6" sx={{ textAlign: "center" }} gutterBottom>
        {title}
      </Typography>
      {children}
    </CardContent>
  </Card>
);

const styles = createStyleSheet({
  clientContextBox: {
    border: "1px solid lightgrey",
    borderRadius: 1.5,
    flex: 1,
    marginTop: 2,
    overflow: "auto",
    padding: 2,
  },
});

export default ClientContext;
